import { LogManager } from '@src/utility/Logger';

import type { GraphQLClient, RequestDocument } from 'graphql-request';

const PAGE_SIZE = 100; // DatoCMS GraphQL max records per page

const logger = LogManager.getLogger('utility:paginateGraphql');

/**
 * Fetches every record from a DatoCMS `all*` collection query by looping
 * first/skip pages until a short page is returned.
 *
 * DatoCMS's GraphQL API returns only the first 20 records when no `first:`
 * argument is supplied, and caps a single page at 100. This helper pages
 * through the full collection so callers are not silently truncated.
 *
 * The query MUST accept `$first: IntType` and `$skip: IntType` and apply them
 * to the collection field named by `collectionKey`.
 */
export async function fetchAllGraphqlPages<T>(
  gqlClient: GraphQLClient,
  query: RequestDocument,
  collectionKey: string,
  variables: Record<string, unknown> = {},
): Promise<T[]> {
  const all: T[] = [];
  let skip = 0;
  for (;;) {
    const res = await gqlClient.request<Record<string, T[]>>(query, {
      ...variables,
      first: PAGE_SIZE,
      skip,
    });
    // A missing key (vs. a present-but-empty array) signals a mismatched
    // collectionKey or renamed query — surface it instead of silently
    // returning no records.
    if (!(collectionKey in res)) {
      logger.warn('Collection key absent from GraphQL response', {
        collectionKey,
        responseKeys: Object.keys(res),
      });
      break;
    }
    const page = res[collectionKey] ?? [];
    all.push(...page);
    if (page.length < PAGE_SIZE) break;
    skip += PAGE_SIZE;
  }
  return all;
}
